<template>
    <div>
        <div class="d-flex mb-3">
            <div v-if="isCharSetType">
                <div class="mb-3">
                    <pc-expand-list
                        always-expanded
                        v-bind:items="charSetsToDisplay"
                        v-bind:header="characterSetsRuleDisplay"
                        v-bind:empty-message="noCharacterSetsMessage">
                    </pc-expand-list>
                </div>
                <div>
                    <i18n-t
                        scope="global"
                        keypath="textValidationRulesTab.maxLengthRuleDisplay"
                        tag="div">
                        <template v-slot:maxLengthLabel>
                            <span class="fw-bold">
                                {{ maxLengthRuleDisplayLabel }}
                            </span>
                        </template>
                        <template v-slot:maxLength>
                            <span v-bind:class="maxLengthValueClass">
                                {{ maxLengthDisplayValue }}
                            </span>
                        </template>
                    </i18n-t>
                </div>
            </div>
            <div
                v-if="isPatternMatchType"
                class="mb-3">
                <pc-expand-list
                    always-expanded
                    use-bullets
                    v-bind:items="patternsToDisplay"
                    v-bind:header="patternsDefinedRuleDisplay"
                    v-bind:empty-message="noPatternsDefined">
                </pc-expand-list>
            </div>
        </div>
        <div>
            <pc-label
                v-bind:label="validationErrorMessageLabel"
                class="fw-bold" />
        </div>
        <div v-if="!isCustomValidationErrorMessageDefined">
            <i18n-t
                scope="global"
                keypath="textValidationRulesTab.defaultErrorMessageLabel"
                tag="div">
                <template v-slot:defaultText>
                    <span>
                        {{ defaultTextTag }}
                    </span>
                </template>
                <template v-slot:message>
                    <span class="text-muted">
                        {{ defaultErrorMessage }}
                    </span>
                </template>
            </i18n-t>
        </div>
        <div
            v-else
            class="text-muted">
            {{ textValidationRule.ValidationErrorMessage }}
        </div>
        <div
            v-if="canEditDataFields"
            class="d-flex">
            <pc-button-row class="mt-3">
                <pc-button
                    primary
                    v-on:click="$emit('edit', textValidationRule.PublicId)">
                    {{ editButtonLabel }}
                </pc-button>
                <pc-button v-on:click="$emit('copy', textValidationRule.PublicId)">
                    {{ copyButtonLabel }}
                </pc-button>
                <pc-button v-on:click="$emit('delete', textValidationRule.PublicId)">
                    {{ deleteButtonLabel }}
                </pc-button>
            </pc-button-row>
        </div>
    </div>
</template>

<script>
    import { mapI18n } from "Util/i18n";
    import { NAMESPACE, KEYS } from "./i18n/textValidationRulesTab.i18n";
    import { TEXT_VALIDATION_RULE_TYPES, TEXT_VALIDATION_RULE_CHAR_SETS } from "./textValidationRulesTab.vue";

    export default {
        props: {
            textValidationRule: { type: Object, required: true },
            canEditDataFields: { type: Boolean, required: true }
        },
        emits: ["edit", "delete", "copy"],
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            hasMaxLengthDefined() {
                const maxLengthValue = this.textValidationRule.RuleSchema.MaxLength;
                return maxLengthValue !== null && maxLengthValue !== "";
            },
            maxLengthDisplayValue() {
                return this.hasMaxLengthDefined
                    ? this.textValidationRule.RuleSchema.MaxLength
                    : this.noMaxLengthDefined;
            },
            maxLengthValueClass() {
                return this.hasMaxLengthDefined ? "" : "text-muted";
            },
            isCharSetType() {
                return this.textValidationRule.TextValidationRuleTypeId === TEXT_VALIDATION_RULE_TYPES.CHAR_SET;
            },
            isPatternMatchType() {
                return this.textValidationRule.TextValidationRuleTypeId === TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH;
            },
            charSetsToDisplay() {
                const charSets = this.textValidationRule.RuleSchema.CharacterSets;
                const setsToDisplay = [];

                for (const charSet of charSets) {
                    setsToDisplay.push(this.getCharSetName(charSet));
                }

                return setsToDisplay;
            },
            patternsToDisplay() {
                const ruleSchema = this.textValidationRule.RuleSchema;
                let patternsToDisplay = [];

                if (ruleSchema != null && ruleSchema.AllowedPatterns != null) {
                    patternsToDisplay = ruleSchema.AllowedPatterns;
                }

                return patternsToDisplay;
            },
            isCustomValidationErrorMessageDefined() {
                return (
                    this.textValidationRule.ValidationErrorMessage &&
                    this.textValidationRule.ValidationErrorMessage.trim() !== ""
                );
            },
            defaultErrorMessage() {
                return this.isCharSetType
                    ? this.defaultCharSetValidationError
                    : this.defaultPatternMatchValidationError;
            }
        },
        methods: {
            getCharSetName(charSetId) {
                switch (charSetId) {
                    case TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_LOWER:
                        return this.aToZLowerLabel;
                    case TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_UPPER:
                        return this.aToZUpperLabel;
                    case TEXT_VALIDATION_RULE_CHAR_SETS.NUMERIC:
                        return this.numericLabel;
                    case TEXT_VALIDATION_RULE_CHAR_SETS.PUNCTUATION:
                        return this.punctuationLabel;
                    default:
                        return charSetId;
                }
            },
            getTextValidationRuleTypeName(tvrType) {
                switch (tvrType) {
                    case TEXT_VALIDATION_RULE_TYPES.CHAR_SET:
                        return this.characterSetTypeName;
                    case TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH:
                        return this.patternMatchTypeName;
                    default:
                        return "";
                }
            }
        }
    };
</script>
